import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { useMemo } from 'react';
import {
  delaysAnalysisQueryOptions,
  peopleAnalysisQueryOptions,
  projectsAnalysisQueryOptions,
  tasksAnalysisQueryOptions,
  throughputQueryOptions,
} from '~/api/analysis';
import { useMe } from '~/api/auth';
import { departmentsQueryOptions } from '~/api/people';
import { useServerClock } from '~/api/serverClock';
import { ThroughputChart } from '~/features/analytics/ThroughputChart';
import { UsageTable } from '~/features/analytics/UsageTable';
import { OccurrenceSheet } from '~/features/work/OccurrenceSheet';
import { addDays, calendarDate, formatMinutes } from '~/lib/format';
import { Avatar } from '~/ui/Avatar';
import { Bar } from '~/ui/Bar';
import { Button } from '~/ui/Button';
import { Card } from '~/ui/Card';
import { Chips } from '~/ui/Chips';
import { Donut } from '~/ui/Donut';
import { EmptyState } from '~/ui/EmptyState';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { PageHeader } from '~/ui/PageHeader';
import { Pill } from '~/ui/Pill';
import { Skeleton } from '~/ui/Skeleton';
import { StatCard } from '~/ui/StatCard';

const PERIODS = ['7', '30', '90'] as const;
type Period = (typeof PERIODS)[number];

interface Search {
  period: Period;
  department?: string;
  run?: string;
}

export const Route = createFileRoute('/_app/analytics')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    period: (PERIODS as readonly string[]).includes(String(s['period']))
      ? (s['period'] as Period)
      : '30',
    department:
      typeof s['department'] === 'string' && s['department'] ? s['department'] : undefined,
    run: typeof s['run'] === 'string' && s['run'] ? s['run'] : undefined,
  }),
  component: AnalyticsPage,
});

const SEGMENT_COLORS = [
  'var(--accent)',
  'var(--accent-ink)',
  'var(--done)',
  'var(--warn)',
  'var(--ink-3)',
  'var(--line)',
];

/** The numbers for managers and the owner, each against the previous period of the same length. */
function AnalyticsPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/analytics' });
  const now = useServerClock(60_000);
  const today = calendarDate(now, me.person.tz ?? me.workspace.tz);
  const from = addDays(today, -(Number(search.period) - 1));
  const range = { from, to: today, departmentId: search.department };
  const go = (patch: Partial<Search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const departments = useQuery({ ...departmentsQueryOptions, enabled: me.person.role === 'owner' });
  const throughput = useQuery(throughputQueryOptions(range));
  const people = useQuery(peopleAnalysisQueryOptions(range));
  const tasks = useQuery(tasksAnalysisQueryOptions({ ...range, limit: 6 }));
  const delays = useQuery(delaysAnalysisQueryOptions(range));
  const projects = useQuery(projectsAnalysisQueryOptions);

  const t = throughput.data?.totals;
  const pct = (v: number | null | undefined) =>
    v === null || v === undefined ? null : Math.round(v * 100);
  const contributors = useMemo(
    () =>
      [...(people.data?.people ?? [])]
        .sort((a, b) => b.completed - a.completed || b.minutesInDuties - a.minutesInDuties)
        .slice(0, 6),
    [people.data],
  );
  const byProject = useMemo(() => {
    const rows = (projects.data ?? [])
      .map((x) => ({ label: x.project.name, value: x.summary.hours.spentMinutes }))
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value);
    const top = rows.slice(0, 5);
    const rest = rows.slice(5).reduce((n, r) => n + r.value, 0);
    if (rest > 0) top.push({ label: 'Other', value: rest });
    return top.map((r, i) => ({ ...r, color: SEGMENT_COLORS[i % SEGMENT_COLORS.length]! }));
  }, [projects.data]);
  const totalProjectMinutes = byProject.reduce((n, s) => n + s.value, 0);

  const exportCsv = () => {
    const rows = people.data?.people ?? [];
    const header = [
      'Name',
      'Completed',
      'On time',
      'On-time rate',
      'Over budget',
      'Delays',
      'Missed',
      'Skipped',
      'Minutes in duties',
    ];
    const lines = rows.map((p) => [
      p.name,
      p.completed,
      p.onTime,
      p.onTimeRate ?? '',
      p.overBudget,
      p.delays,
      p.missed,
      p.skipped,
      p.minutesInDuties,
    ]);
    const csv = [header, ...lines]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `people-${from}-to-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-[1240px]">
      <PageHeader
        eyebrow="Numbers"
        title="Analytics"
        subtitle={`${from} to ${today}, against the ${search.period} days before.`}
        actions={
          <>
            <span data-tour="ana-range">
              <Chips
                label="Period"
                value={search.period}
                onChange={(p) => go({ period: p })}
                options={PERIODS.map((p) => ({ value: p, label: `${p}D` }))}
              />
            </span>
            <Button
              variant="secondary"
              icon={<Download size={16} aria-hidden />}
              data-tour="ana-export"
              onClick={exportCsv}
              disabled={!people.data}
            >
              Export CSV
            </Button>
          </>
        }
      />

      {me.person.role === 'owner' && (departments.data?.length ?? 0) > 0 && (
        <div className="mb-5">
          <Chips
            label="Department"
            value={search.department ?? 'all'}
            onChange={(d) => go({ department: d === 'all' ? undefined : d })}
            options={[
              { value: 'all', label: 'Everyone' },
              ...(departments.data ?? []).map((d) => ({
                value: d.id,
                label: d.name,
                count: d.personCount,
              })),
            ]}
          />
        </div>
      )}

      {throughput.isError ? (
        <ErrorNotice error={throughput.error} onRetry={() => throughput.refetch()} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-tour="ana-stats">
            <StatCard
              lead
              label="Tasks completed"
              value={t?.done ?? 0}
              delta={t ? t.done - t.donePrevious : null}
              deltaLabel="vs previous"
            />
            <StatCard
              label="Hours tracked"
              value={Math.round((t?.minutes ?? 0) / 60)}
              delta={t ? Math.round((t.minutes - t.minutesPrevious) / 60) : null}
              deltaLabel="hours vs previous"
              delay={40}
            />
            <StatCard
              label="On-time delivery"
              value={pct(t?.onTimeRate) ?? 0}
              note={t?.onTimeRate === null ? 'Nothing finished yet' : undefined}
              delta={
                t && t.onTimeRate !== null && t.onTimeRatePrevious !== null
                  ? pct(t.onTimeRate)! - pct(t.onTimeRatePrevious)!
                  : null
              }
              deltaLabel="points vs previous"
              delay={80}
            />
            <StatCard
              label="Avg cycle time (min)"
              value={t?.cycleMedianMinutes ?? 0}
              note={
                t?.cycleMedianMinutes === null ? 'Start to finish, once there are runs' : undefined
              }
              delta={
                t && t.cycleMedianMinutes !== null && t.cycleMedianMinutesPrevious !== null
                  ? t.cycleMedianMinutes - t.cycleMedianMinutesPrevious
                  : null
              }
              deltaLabel="minutes vs previous"
              delay={120}
            />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <Card
              title="Throughput"
              eyebrow="Finished per day, 7-day average, previous period"
              className="lg:col-span-2"
              dataTour="ana-throughput"
              delay={160}
            >
              {throughput.data ? (
                <ThroughputChart
                  days={throughput.data.days}
                  previous={throughput.data.previousDays}
                />
              ) : (
                <Skeleton lines={6} />
              )}
            </Card>
            <Card title="Time by project" eyebrow="This period's hours" delay={200}>
              {projects.isPending ? (
                <Skeleton lines={5} />
              ) : byProject.length === 0 ? (
                <p className="text-sm text-ink-3">No project time yet.</p>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <Donut percent={100} segments={byProject} label="tracked" />
                  <ul className="w-full text-xs">
                    {byProject.map((s) => (
                      <li key={s.label} className="flex items-center gap-2 py-1">
                        <span
                          aria-hidden
                          className="size-2.5 rounded-full"
                          style={{ background: s.color }}
                        />
                        <span className="flex-1 truncate">{s.label}</span>
                        <span className="font-mono tabular text-ink-2">
                          {formatMinutes(s.value)}
                        </span>
                        <span className="w-10 text-right font-mono tabular text-ink-3">
                          {Math.round((s.value / totalProjectMinutes) * 100)}%
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <Card
              title="Top contributors"
              eyebrow="Most finished"
              dataTour="ana-contributors"
              delay={240}
            >
              {people.isPending ? (
                <Skeleton lines={5} />
              ) : contributors.length === 0 ? (
                <p className="text-sm text-ink-3">Nobody has finished a duty in this period.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {contributors.map((p) => (
                    <li key={p.personId} className="flex items-center gap-3">
                      <Avatar name={p.name} size="sm" personId={p.personId} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="truncate font-medium">{p.name}</span>
                          <span className="font-mono text-xs tabular text-ink-2">
                            {p.completed}
                          </span>
                        </div>
                        <Bar
                          value={p.completed}
                          max={contributors[0]!.completed || 1}
                          className="mt-1"
                        />
                        <p className="mt-1 text-[11px] text-ink-3">
                          {formatMinutes(p.minutesInDuties)}
                          {p.onTimeRate !== null && ` · ${Math.round(p.onTimeRate * 100)}% on time`}
                          {p.delays > 0 && ` · ${p.delays} delayed`}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Tasks that overrun" eyebrow="By share of runs over budget" delay={280}>
              {tasks.isPending ? (
                <Skeleton lines={5} />
              ) : (tasks.data?.tasks.length ?? 0) === 0 ? (
                <p className="text-sm text-ink-3">No finished runs to judge yet.</p>
              ) : (
                <ul className="divide-y divide-line text-sm">
                  {tasks.data!.tasks.map((x) => (
                    <li key={x.taskId} className="flex items-center gap-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{x.title}</p>
                        <p className="text-xs text-ink-3">
                          {x.runs} runs · usually{' '}
                          {x.medianActualMinutes === null
                            ? '—'
                            : formatMinutes(x.medianActualMinutes)}
                          {x.budgetMinutes !== null && ` of ${formatMinutes(x.budgetMinutes)}`}
                        </p>
                      </div>
                      <Pill
                        tone={x.overrunRate >= 0.5 ? 'late' : x.overrunRate > 0 ? 'warn' : 'done'}
                      >
                        {Math.round(x.overrunRate * 100)}% over
                      </Pill>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card
              title="Delays"
              eyebrow={
                delays.data
                  ? `${delays.data.acknowledged} of ${delays.data.total} acknowledged`
                  : 'Reasons given'
              }
              delay={320}
            >
              {delays.isPending ? (
                <Skeleton lines={5} />
              ) : delays.isError ? (
                <ErrorNotice error={delays.error} onRetry={() => delays.refetch()} />
              ) : delays.data.total === 0 ? (
                <EmptyState title="No delays in this period." />
              ) : (
                <div className="flex flex-col gap-3">
                  <ul className="flex flex-col gap-1.5 text-sm">
                    {delays.data.topReasons.map((r) => (
                      <li key={r.text} className="flex gap-2">
                        <span className="font-mono text-xs text-ink-3 tabular">{r.count}×</span>
                        <span className="text-ink-2">{r.text}</span>
                      </li>
                    ))}
                  </ul>
                  <ul className="divide-y divide-line text-xs">
                    {delays.data.occurrences.slice(0, 5).map((o) => (
                      <li key={o.id}>
                        <button
                          type="button"
                          onClick={() => go({ run: o.id })}
                          className="-mx-2 flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left hover:bg-paper-2"
                        >
                          <span className="flex-1 truncate">{o.taskTitle}</span>
                          <span className="text-ink-3">{o.personName}</span>
                          {!o.delayReviewedAt && <Pill tone="warn">Unseen</Pill>}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      {(me.person.role === 'owner' || me.person.role === 'manager') && (
        <UsageTable range={range} tz={me.workspace.tz} />
      )}

      <OccurrenceSheet
        id={search.run ?? null}
        today={today}
        onClose={() => go({ run: undefined })}
        onOpenTask={() => go({ run: undefined })}
      />
    </div>
  );
}
